/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  DEFAULT_PICKER_COLOR,
  SUPPORTED_VIDEO_EXTENSIONS
} from '~backoffice/app/~shared/constants/global.constants';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import { ProgramService } from '~core/services/program.service';
import { Subject, finalize, takeUntil, tap } from 'rxjs';
import { Program, ProgramCreationParams } from '~core/models/program.models';
import {
  httpErrorToFormGroupError,
  setErrorToControls
} from '@veone/ngx-utilities/form';

@Component({
  standalone: false,
  selector: 'edit-program-teaser',
  templateUrl: './edit-program-teaser.component.html',
  styleUrls: ['./edit-program-teaser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditProgramTeaserComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() program: Program | null | undefined;
  isLoading = false;

  form = new FormGroup({
    teaserTitle: new FormControl<string | undefined>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    teaserTitleColor: new FormControl<string | undefined>(''),
    teaserDescription: new FormControl<string | undefined>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    teaserDescriptionColor: new FormControl<string | undefined>('', [
      Validators.minLength(3)
    ])
  });

  teaserControl = new FormControl<File | null>(null);

  supportedVideoExtensions = SUPPORTED_VIDEO_EXTENSIONS;

  colors = [DEFAULT_PICKER_COLOR, '#067bfe', '#393a40', '#ffcb3b', '#ff8800'];
  defaultColor = DEFAULT_PICKER_COLOR;

  private _destroy$ = new Subject<void>();
  constructor(
    private programService: ProgramService,
    private snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<EditProgramTeaserComponent>,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.patchFormValues(this.program);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.patchFormValues(changes['program']?.currentValue);
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  patchFormValues(program: Program | null | undefined): void {
    if (program) {
      this.form.patchValue({
        teaserTitle: program.teaser?.title?.value ?? '',
        teaserTitleColor: program.teaser?.title?.color ?? '',
        teaserDescription: program.teaser?.description?.color ?? '',
        teaserDescriptionColor: program.teaser?.description?.color ?? ''
      });
    }
  }

  setColor(
    color: string,
    property: 'teaserTitleColor' | 'teaserDescriptionColor' = 'teaserTitleColor'
  ): void {
    this.form.patchValue({ [property]: color });
  }

  update(): void {
    if (!this.program || this.form.invalid) return;
    this.isLoading = true;
    const values = this.form.value;
    const data = {
      teaser: {
        title: {
          value: values.teaserTitle,
          ...(values.teaserTitleColor && { color: values.teaserTitleColor })
        },
        description: {
          value: values.teaserDescription,
          ...(values.teaserDescriptionColor && {
            color: values.teaserDescriptionColor
          })
        },
        ...(this.teaserControl.value && {
          file: this.teaserControl.value
        })
      }
    };

    this.programService
      .update(this.program.id, data as unknown as ProgramCreationParams)
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: program => {
            this.dialogRef.close(program);
            this.snackbarService.open({
              text: `Données de teaser mises à jour`,
              type: 'success'
            });
          },
          error: error => {
            const formGroupError = httpErrorToFormGroupError(error);
            const teaserError = formGroupError.find(el =>
              el.propertyPath.includes('file')
            );
            if (teaserError) {
              this.teaserControl.setErrors({
                httpError: teaserError.title
              });
              return;
            }
            const teaserTitleError = formGroupError.find(
              el =>
                el.propertyPath.includes('teaser') &&
                el.propertyPath.includes('title')
            );
            if (teaserTitleError) {
              this.form.get('teaserTitle')?.setErrors({
                httpErrors: teaserTitleError.title
              });
            }
            const teaserDescriptionError = formGroupError.find(
              el =>
                el.propertyPath.includes('teaser') &&
                el.propertyPath.includes('description')
            );
            if (teaserDescriptionError) {
              this.form.get('teaserDescription')?.setErrors({
                httpErrors: teaserDescriptionError.title
              });
            }
            setErrorToControls(this.form, formGroupError);
          }
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  setFile(fileType: 'teaser' | 'teaser', files: File[] | null): void {
    if (fileType === 'teaser') {
      this.teaserControl.patchValue(files ? files[0] : null);
      return;
    }
    this.teaserControl.patchValue(files ? files[0] : null);
    this.resetFormErrors();
  }

  resetFormErrors(): void {
    const keys = Object.keys(this.form.controls);
    keys.forEach(key => {
      this.form.get(key)?.setErrors(null);
      this.form.get(key)?.updateValueAndValidity();
    });
    this.cdRef.detectChanges();
  }

  get modalTitle(): string {
    return 'Configuration du teaser du programme';
  }

  get modalDescription(): string {
    return 'Veuillez éditer les champs ci-dessous pour mettre à jour le teaser';
  }

  get isFormValid(): boolean {
    return this.program?.teaser
      ? this.form.valid
      : this.form.valid && !!this.teaserControl.value;
  }
}
